/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';

import { ICondition, IMedicationRequest, IObservation, IProcedure } from '@ahryman40k/ts-fhir-types/lib/R4/Resource';

import {
  selectCurrentPaneState,
  selectPatientSummaryState,
  togglePaneView,
} from '../PatientSummary/patientSummarySlice';
import { contextualizationText, summaryText, topicText } from '../../../utils';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import {
  selectFhirMedicines,
  selectFhirProblemList,
  selectFhirMedicalHx,
  selectFhirVitals,
  selectIsFhirVisible,
  selectFhirSurgicalHx,
} from '../../../Fhir/fhirSlice';
import { ToggleOffIcon, ToggleOnIcon } from './ToggleButtonIcons';
import styles from './ToggleButtons.module.scss';
import { showPatientMedicines, useEnableEpicEmbeddedView } from '../../../permissions';
import SummaryDrawer from './SummaryDrawer';
import { useState } from 'react';
import Button from '@mui/material/Button';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'var(--threepane-button-text)',
    fontSize: '12px',
    marginTop: '5px !important',
    transform: 'translateX(0%) !important',
    maxWidth: '190px',
    boxShadow: theme.shadows[2],
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
    transform: 'translateX(350%) !important',
    width: '15px',
    height: '12px',
    marginTop: '-11px !important',
    marginRight: '5px !important',
  },
}));

/**
 * Is FHIR 2nd column (which has patient chart data like vitals, meds, medicalhx) visible
 */
export const isPatientChartPaneVisible = (
  patientMedicationFlagEnabled: boolean,
  vitals: IObservation[] | undefined,
  medicines: IMedicationRequest[] | undefined,
  medicalHx: ICondition[] | undefined,
  problemList: ICondition[] | undefined,
  surgicalHx: IProcedure[] | undefined
) => {
  return patientMedicationFlagEnabled && (vitals || medicines || problemList || medicalHx || surgicalHx);
};

export const ThreePaneViewToggleButtons = ({ title, props }: { title?: string; props?: 'view_summary' }) => {
  const [open, setOpen] = useState(true);
  const isFhirVisible = useAppSelector(selectIsFhirVisible);
  const patientMedicationFlagEnabled = showPatientMedicines();
  const vitals = useAppSelector(selectFhirVitals);
  const medicines = useAppSelector(selectFhirMedicines);
  const problemList = useAppSelector(selectFhirProblemList);
  const surgicalHx = useAppSelector(selectFhirSurgicalHx);

  const medicalHx = useAppSelector(selectFhirMedicalHx);

  const dispatch = useAppDispatch();
  const paneViewData = useAppSelector(selectCurrentPaneState);
  const enableEpicEmbeddedView = useEnableEpicEmbeddedView();
  const { summary } = useAppSelector(selectPatientSummaryState);

  const buttons: { id: keyof typeof paneViewData }[] = [
    { id: topicText },
    ...(isFhirVisible ||
    isPatientChartPaneVisible(patientMedicationFlagEnabled, vitals, medicines, medicalHx, problemList, surgicalHx)
      ? [{ id: contextualizationText }]
      : []), // Conditional inclusion - the toggle should be seen even if only the vitals and medication pane is present
    ...(props && !enableEpicEmbeddedView ? [{ id: summaryText }] : []),
  ];

  // Calculate the number of visible panes
  const visiblePaneCount = buttons.filter((button) => paneViewData[button.id]).length;

  // Check if a button should be disabled (when exactly two panels are visible)
  const isButtonDisabled = (id: keyof typeof paneViewData) => visiblePaneCount === 1 && paneViewData[id];

  return (
    <div className={styles.container}>
      {enableEpicEmbeddedView && summary ? (
        <div id="summary-drawer-container" className={styles.summaryDrawerContainer}>
          <Button id="view-summary" variant="text" className={styles.viewSummary} onClick={() => setOpen(!open)}>
            View Summary
          </Button>
          <SummaryDrawer open={open} setOpen={setOpen} />
        </div>
      ) : (
        <>
          <p
            className={styles.headingText}
            style={{
              marginRight: buttons.length === 1 ? 60 : buttons.length === 2 ? 150 : 280,
            }}
          >
            TOGGLE VISUALIZATION
          </p>
          <div className={styles.toggleButtons}>
            {buttons.map((button) => {
              // Determine if the current button should be disabled and if the tooltip should be shown
              const isDisabled = isButtonDisabled(button.id);
              const showTooltip = isDisabled;

              return (
                <LightTooltip
                  key={button.id}
                  title={showTooltip ? 'You can hide up to two panels. At least one panel must remain visible.' : ''}
                >
                  <span className={styles.toggleButtonContainer}>
                    {/* button.id has everything in lowercase, changing the first letter to capital. */}
                    <span className={styles.toggleButtonLabel}>
                      {button.id.charAt(0).toUpperCase() + button.id.slice(1)}
                    </span>
                    <IconButton
                      onClick={() => {
                        if (!isDisabled) {
                          dispatch(togglePaneView(button.id));
                        }
                      }}
                      color="secondary"
                      disableRipple
                      disabled={isDisabled}
                      sx={{ padding: 0 }}
                      data-testid="toggle-view-buttons"
                    >
                      {paneViewData[button.id] ? <ToggleOnIcon /> : <ToggleOffIcon />}
                    </IconButton>
                  </span>
                </LightTooltip>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
